import { useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { HomeLayout, LandingLayout, Handoff } from "@layouts";
import {
  Activate,
  ActiveProjectDetails,
  ActiveProjects,
  ArchivedProjects,
  BudgetTracking,
  CreateWorkspace,
  Help,
  JiraAuth,
  JiraIntegration,
  Legal,
  Library,
  Login,
  Notifications,
  PasswordReset,
  PasswordRetrieve,
  PdfEditor,
  Pricing,
  Printer,
  ProjectEditor,
  ProjectSessions,
  Proposal,
  Register,
  SessionReplayer,
  Settings,
  SharedHelp,
  SSOLink,
  Subscription,
  Team,
  TemplateEditor,
  Templates
} from "@views";
import {
  activate,
  archived,
  budgetTracking,
  createBoard,
  editorRouter,
  help,
  home,
  inviteRegister,
  jiraAuth,
  jiraIntegration,
  library,
  login,
  notifications,
  pdfEditor,
  policy,
  pricing,
  printPdfRouter,
  projectEdit,
  register,
  resetPassword,
  retrievePassword,
  sessionReplay,
  sessions,
  settings,
  settingsApiKeys,
  settingsCompany,
  settingsDomain,
  settingsLabels,
  settingsPassword,
  settingsProject,
  settingsProjectTypes,
  settingsRates,
  settingsTimeEquiv,
  sharedHelp,
  sharedProposalOldRouter,
  sharedProposalRouter,
  ssoActivation,
  subscription,
  team,
  templateEditorRouter,
  templates,
  terms,
} from "@paths";
import {
  ApiMcpKeys,
  Company,
  Domain,
  Labels,
  Password,
  Profile,
  Project,
  ProjectTypes,
  Rates,
  TimeEquiv,
} from "@views/Utility/Settings/subviews";
import { LOCKED_FEATURES } from "@utils";
import { Paper } from "@material-ui/core";
import { ApropoRedirector, RouteProtector } from "@components";
import useStyle from "./Router.style";

export const Router = () => {
  const classes = useStyle();

  const notApropo = useMemo(
    () => window.location.origin !== import.meta.env.APP_APP_ORIGIN_DOMAIN,
    []
  );

  if (notApropo) {
    return (
      <Paper elevation={0} className={classes.root}>
        <BrowserRouter>
          <Routes>
            <Route path={sharedProposalRouter} element={<Proposal/>} />
            <Route path={sharedProposalOldRouter} element={<Proposal/>} />
            <Route path="*" element={<ApropoRedirector/>} />
          </Routes>
        </BrowserRouter>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} className={classes.root}>
      <BrowserRouter>
        <Routes>
          <Route path={sharedProposalRouter} element={<Proposal/>} />
          <Route path={sharedProposalOldRouter} element={<Proposal/>} />
          <Route path={printPdfRouter} element={<Printer/>} />
          <Route path={terms} element={<Legal/>} />
          <Route path={policy} element={<Legal/>} />
          <Route path={sharedHelp} element={<SharedHelp/>} />

          <Route
            path={home}
            element={<HomeLayout/>}
            errorElement={<Navigate to={home} />}
          >
            <Route path={notifications} element={<Notifications/>} />
            <Route path={archived} element={<ArchivedProjects/>} />
            {/*<Route path={boards} element={<Boards />} />*/}
            <Route path={editorRouter} element={<ProjectEditor/>} />
            <Route path={pdfEditor()} element={<PdfEditor/>} />
            <Route path={budgetTracking()} element={<BudgetTracking/>} />
            <Route
              path={subscription}
              element={
                <RouteProtector adminOnly>
                  <Subscription/>
                </RouteProtector>
              }
            />
            <Route
              path={pricing}
              element={
                <RouteProtector adminOnly>
                  <Pricing/>
                </RouteProtector>
              }
            />
            <Route path={team} element={<Team/>} />
            <Route path={templates} element={<Templates/>} />
            <Route path={templateEditorRouter} element={<TemplateEditor/>} />
            <Route path={library} element={<Library/>} />
            {/*<Route path={contact} element={<Contact />} />*/}
            <Route path={help} element={<Help/>} />
            <Route path={jiraIntegration} element={<JiraIntegration/>} />

            {isMobile && (
              <Route path={projectEdit()} element={<ActiveProjectDetails/>} />
            )}

            <Route path={settings} element={<Settings/>}>
              <Route
                path={settingsCompany}
                element={
                  <RouteProtector adminOnly>
                    <Company/>
                  </RouteProtector>
                }
              />
              <Route path={settingsPassword} element={<Password/>} />
              <Route path={settingsApiKeys} element={<ApiMcpKeys/>} />
              <Route path={settings} element={<Profile/>} />
              <Route
                path={settingsProject}
                element={
                  <RouteProtector managerOnly>
                    <Project/>
                  </RouteProtector>
                }
              >
                <Route path={settingsDomain} element={<Domain/>} />
                <Route path={settingsTimeEquiv} element={<TimeEquiv/>} />
                <Route path={settingsRates} element={<Rates/>} />
                <Route path={settingsProjectTypes} element={<ProjectTypes/>} />
                <Route path={settingsLabels} element={<Labels/>} />
              </Route>
              <Route path="*" element={<Navigate to={settings} />} />
            </Route>

            {!isMobile && (
              <>
                <Route
                  path={sessions()}
                  element={
                    <RouteProtector
                      accessedFeature={LOCKED_FEATURES.RECORDINGS}
                    >
                      <ProjectSessions/>
                    </RouteProtector>
                  }
                />
                <Route
                  path={sessionReplay()}
                  element={
                    <RouteProtector
                      accessedFeature={LOCKED_FEATURES.RECORDINGS}
                    >
                      <SessionReplayer/>
                    </RouteProtector>
                  }
                />
              </>
            )}

            <Route path={home} element={<ActiveProjects/>} />
            <Route path="*" element={<Navigate to={home} />} />
          </Route>

          <Route
            path="/"
            element={window.location.hash.startsWith("#/handoff") ? <Handoff/> : <LandingLayout/>}
          >
            <Route path={jiraAuth} element={<JiraAuth/>} />
            <Route path={ssoActivation} element={<SSOLink/>} />
            <Route path={login} element={<Login/>} />
            <Route path={register} element={<Register/>} />
            <Route path={inviteRegister()} element={<Register/>} />
            <Route path={activate} element={<Activate/>} />
            <Route path={retrievePassword} element={<PasswordRetrieve/>} />
            <Route path={resetPassword} element={<PasswordReset/>} />
            <Route path={createBoard} element={<CreateWorkspace/>} />
            <Route path="*" element={<Navigate to={login} />} />
          </Route>
          <Route path="*" element={<Navigate to={home} />} />
        </Routes>
      </BrowserRouter>
    </Paper>
  );
};
